
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { ServiceVisit } from "@/types";
import { formatDistanceToNow, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReviewForm from "../reviews/ReviewForm";
import { StarRating } from "../reviews/StarRating";
import { Calendar } from "lucide-react";
import { downloadVisitCalendar } from "@/utils/calendarUtils";

interface ServiceHistoryCardProps {
  visit: ServiceVisit;
}

export default function ServiceHistoryCard({ visit }: ServiceHistoryCardProps) {
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const handleReviewSubmitted = (review: any) => {
    // In a real app, we would update the visit with the review
    setIsReviewOpen(false);
  };

  const handleAddToCalendar = () => {
    downloadVisitCalendar(visit);
  };

  return (
    <Card className={`mb-4 ${
      visit.status === 'scheduled' ? 'border-l-4 border-l-sage-500' :
      visit.status === 'in-progress' ? 'border-l-4 border-l-secondary' :
      visit.status === 'completed' ? 'border-l-4 border-l-primary/60' :
      'border-l-4 border-l-muted'
    }`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">{format(new Date(visit.date), 'MMMM d, yyyy')}</CardTitle>
          <span className={`px-2 py-1 text-xs rounded-full ${
            visit.status === 'scheduled' ? 'bg-sage-100 text-sage-800' :
            visit.status === 'in-progress' ? 'bg-secondary/20 text-secondary-foreground' :
            visit.status === 'completed' ? 'bg-primary/10 text-primary' :
            'bg-muted text-muted-foreground'
          }`}>
            {visit.status.charAt(0).toUpperCase() + visit.status.slice(1)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          {visit.status === 'scheduled' 
            ? `Coming up ${formatDistanceToNow(new Date(visit.date), { addSuffix: true })}` 
            : visit.status === 'in-progress'
            ? 'Technician currently on site'
            : visit.status === 'completed'
            ? `Completed ${formatDistanceToNow(new Date(visit.date), { addSuffix: true })}`
            : 'Cancelled'}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Plants:</span>
            <span className="font-medium">{visit.actualPlantCount || visit.plantCount}</span>
          </div>
          
          {visit.technicianName && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Technician:</span>
              <span className="font-medium">{visit.technicianName}</span>
            </div>
          )}
          
          {visit.checkInTime && visit.checkOutTime && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Duration:</span>
              <span className="font-medium">
                {visit.duration ? `${visit.duration} minutes` : 'N/A'}
              </span>
            </div>
          )}

          {visit.status === 'completed' && visit.notes && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium mb-1">Notes:</p>
              <p className="text-sm">{visit.notes}</p>
            </div>
          )}

          {visit.status === 'completed' && visit.issues && visit.issues.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium mb-2">Issues Found:</p>
              <ul className="text-sm space-y-1">
                {visit.issues.map((issue, index) => (
                  <li key={index}>
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                      issue.severity === 'high' ? 'bg-destructive' :
                      issue.severity === 'medium' ? 'bg-secondary' :
                      'bg-sage-300'
                    }`}></span>
                    {issue.type}: {issue.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {visit.review && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium mb-2">Your Rating:</p>
              <StarRating maxStars={5} value={visit.review.rating} readonly size="small" />
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-between pt-2">
        {visit.status === 'completed' && !visit.review && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsReviewOpen(true)}
          >
            Leave Review
          </Button>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto gap-1"
          onClick={handleAddToCalendar}
        >
          <Calendar className="h-4 w-4" />
          Add to Calendar
        </Button>
      </CardFooter>
      
      <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate Your Service</DialogTitle>
          </DialogHeader>
          <ReviewForm visit={visit} onReviewSubmitted={handleReviewSubmitted} />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
